var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { ButtonGroup, Toolbar, ToolbarItem, ToolbarSeparator } from '@progress/kendo-react-buttons';
import { EditorState, Plugin, PluginKey } from '@progress/kendo-editor-common/prosemirror/state';
import { EditorView } from '@progress/kendo-editor-common/prosemirror/view';
import { Schema } from '@progress/kendo-editor-common/prosemirror/model';
import { baseKeymap } from '@progress/kendo-editor-common/prosemirror/commands';
import { marks, nodes } from './config/schema';
import { defaultStyle, tablesStyles, rtlStyles } from './config/defaultStyles';
import { keymap } from '@progress/kendo-editor-common/prosemirror/keymap';
import { history } from '@progress/kendo-editor-common/prosemirror/history';
import { dropCursor } from '@progress/kendo-editor-common/prosemirror/dropcursor';
import { gapCursor } from '@progress/kendo-editor-common/prosemirror/gapcursor';
import { EditorToolsSettings } from './config/toolsSettings';
import { EditorDialogs } from './dialogs/main';
import { getMark, parseContent } from '@progress/kendo-editor-common';
import { tableEditing } from '@progress/kendo-editor-common/prosemirror/tables';
import { EditorUtils } from './utils';
var link = EditorToolsSettings.link, image = EditorToolsSettings.image, viewHtml = EditorToolsSettings.viewHtml, bold = EditorToolsSettings.bold, italic = EditorToolsSettings.italic, underline = EditorToolsSettings.underline;
var firefox = typeof window !== 'undefined' &&
    /Firefox/.test(window.navigator.userAgent);
/**
 * Represents the [KendoReact Editor component]({% slug overview_editor %}).
 *
 * @example
 * ```jsx
 * class App extends React.Component {
 *    render() {
 *       return (
 *           <Editor
 *               defaultContent="<p>Hello World</p>"
 *               tools={[
 *                  [ EditorTools.Bold, EditorTools.Italic ]
 *               ]}
 *           />
 *       );
 *    }
 * }
 * ReactDOM.render(<App />, document.querySelector('my-app'));
 * ```
 */
var Editor = /** @class */ (function (_super) {
    __extends(Editor, _super);
    function Editor() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        /**
         * @hidden
         */
        _this.state = {
            view: undefined,
            linkDialog: false,
            imageDialog: false,
            viewHtmlDialog: false
        };
        _this.renderDialog = function (Component, settings, stateFlag) {
            return _this.state[stateFlag] && (React.createElement(Component, { view: _this.state.view, settings: settings, dir: _this.props.dir, onClose: function () {
                    var _a;
                    return _this.setState((_a = {}, _a[stateFlag] = false, _a));
                } }));
        };
        _this.renderTool = function (Tool, index) {
            var tool = (React.createElement(Tool, { view: _this.state.view, dir: _this.props.dir, key: index }));
            return tool.type === ToolbarSeparator ? React.createElement(Tool, { key: index }) : tool;
        };
        _this.updateTools = function (view, _prevViewState) {
            _this.setState({ view: view });
        };
        _this.onPasteHtml = function (html) {
            if (_this.props.onPasteHtml) {
                var newHtml = _this.props.onPasteHtml.call(undefined, {
                    target: _this,
                    pastedHtml: html
                });
                if (typeof newHtml === 'string') {
                    return newHtml;
                }
            }
            return html;
        };
        return _this;
    }
    Object.defineProperty(Editor.prototype, "element", {
        /**
         * Returns the DOM element of the Editor.
         */
        get: function () {
            return this._element;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Editor.prototype, "contentElement", {
        /**
         * Returns the content-editable DOM element of the Editor.
         */
        get: function () {
            return this._contentElement;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Editor.prototype, "view", {
        /**
         * Returns the `view` object of the Editor.
         */
        get: function () {
            return this.state.view;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    Editor.prototype.componentDidMount = function () {
        if (!this.iframe || !firefox) {
            this.initialize();
        }
    };
    /**
     * @hidden
     */
    Editor.prototype.componentWillUnmount = function () {
        if (this.state.view) {
            this.state.view.destroy();
        }
    };
    /**
     * @hidden
     */
    Editor.prototype.focus = function () {
        if (this.state.view) {
            this.state.view.focus();
        }
    };
    /**
     * @hidden
     */
    Editor.prototype.render = function () {
        var _this = this;
        var _a = this.props, _b = _a.tools, tools = _b === void 0 ? Editor.defaultProps.tools : _b, _c = _a.defaultEditMode, defaultEditMode = _c === void 0 ? Editor.defaultProps.defaultEditMode : _c, _d = _a.contentStyle, contentStyle = _d === void 0 ? Editor.defaultProps.contentStyle : _d, className = _a.className;
        var buttons = tools.map(function (item, index) {
            return Array.isArray(item) ?
                React.createElement(ButtonGroup, null, item.map(_this.renderTool, index)) :
                _this.renderTool(item, index);
        });
        return (React.createElement("div", { ref: function (e) { return _this._element = e; }, className: 'k-widget k-editor' + (className ? " " + className : ''), dir: this.props.dir, style: this.props.style },
            buttons.length > 0 && React.createElement(Toolbar, null, buttons.map(function (item, i) {
                if (item.type === ToolbarSeparator) {
                    return item;
                }
                return React.createElement(ToolbarItem, { className: "k-tool-group", key: i }, item);
            })),
            defaultEditMode === 'iframe' ?
                (React.createElement("iframe", { onLoad: firefox ? function () {
                        _this.initialize();
                    } : undefined, ref: function (e) { return _this.iframe = e; }, frameBorder: "0", style: contentStyle, className: "k-content" })) :
                (React.createElement("div", { style: contentStyle, className: "k-content" },
                    React.createElement("div", { ref: function (e) { return _this._contentElement = e; }, style: {
                            boxSizing: 'border-box',
                            overflowY: 'scroll',
                            padding: '0 4px'
                        }, className: "k-editable-area", suppressContentEditableWarning: true }))),
            this.renderDialog(EditorDialogs.InsertLinkDialog, link, 'linkDialog'),
            this.renderDialog(EditorDialogs.InsertImageDialog, image, 'imageDialog'),
            this.renderDialog(EditorDialogs.ViewHtmlDialog, viewHtml, 'viewHtmlDialog')));
    };
    Editor.prototype.initialize = function () {
        var _this = this;
        var iframeWindow = this.iframe && this.iframe.contentWindow;
        if (iframeWindow) {
            var iframeDocument_1 = iframeWindow.document;
            [defaultStyle, tablesStyles, this.props.dir === 'rtl' ? rtlStyles : undefined].forEach(function (styles) {
                if (styles) {
                    var style = iframeDocument_1.createElement('style');
                    style.appendChild(iframeDocument_1.createTextNode(styles));
                    iframeDocument_1.head.appendChild(style);
                }
            });
            var meta = iframeDocument_1.createElement('meta');
            meta.setAttribute('charset', 'utf-8');
            iframeDocument_1.head.appendChild(meta);
            // The content has to be wrapped because the `dropCursor` plugin raises a `body.offsetParent is null` error.
            this._contentElement = iframeDocument_1.createElement('div');
            iframeDocument_1.body.appendChild(this._contentElement);
            this._contentElement.classList.add('k-content');
        }
        var dom = this._contentElement;
        if (!dom) {
            return;
        }
        var target = this;
        var schema = new Schema({ nodes: nodes, marks: marks });
        var plugins = [
            // https://prosemirror.net/docs/ref/#state.PluginSpec
            new Plugin({
                view: function () { return ({ update: _this.updateTools }); },
                key: new PluginKey('toolbar-tools-update-plugin')
            }),
            new Plugin({
                filterTransaction: function (transaction, state) {
                    return (_this.props.onExecute &&
                        _this.props.onExecute.call(undefined, { target: target, transaction: transaction, state: state })) !== false;
                },
                key: new PluginKey('onExecute-event-plugin')
            }),
            history(),
            dropCursor(),
            gapCursor(),
            tableEditing()
        ];
        var shortcuts = __assign({}, EditorUtils.getShortcuts({
            types: { listItem: 'list_item', hardBreak: 'hard_break' },
            toolsSettings: { bold: bold, italic: italic, underline: underline }
        }), { 'Mod-k': function () {
                var _a = _this.state, linkDialog = _a.linkDialog, editorView = _a.view;
                if (editorView) {
                    var editorState = editorView.state;
                    var collapsed = editorState.selection.empty;
                    var linkMark = getMark(editorState, editorState.schema.marks[link.mark]);
                    var disabled = collapsed && !linkMark;
                    if (!linkDialog && !disabled) {
                        _this.setState({ linkDialog: true });
                    }
                }
                return !linkDialog;
            } });
        var _a = this.props, _b = _a.defaultContent, defaultContent = _b === void 0 ? Editor.defaultProps.defaultContent : _b, onMount = _a.onMount;
        var doc = parseContent(defaultContent, schema, { preserveWhitespace: 'full' });
        var viewProps = {
            state: EditorState.create({ doc: doc, plugins: plugins.concat([keymap(shortcuts), keymap(baseKeymap)]) }),
            transformPastedHTML: this.onPasteHtml
        };
        var view = (onMount && onMount.call(undefined, { plugins: plugins, shortcuts: shortcuts, target: target, viewProps: viewProps, dom: dom })) ||
            new EditorView({ mount: dom }, viewProps);
        this.setState({
            view: view
        });
    };
    /**
     * @hidden
     */
    Editor.propTypes = {
        defaultContent: PropTypes.string,
        defaultEditMode: PropTypes.oneOf(['iframe', 'div']),
        contentStyle: PropTypes.object,
        dir: PropTypes.string,
        className: PropTypes.string,
        style: PropTypes.object,
        tools: PropTypes.arrayOf(PropTypes.any),
        onMount: PropTypes.func,
        onPasteHtml: PropTypes.func,
        onExecute: PropTypes.func
    };
    /**
     * @hidden
     */
    Editor.defaultProps = {
        defaultContent: '',
        defaultEditMode: 'iframe',
        contentStyle: {
            height: '300px'
        },
        tools: []
    };
    return Editor;
}(React.Component));
export default Editor;
//# sourceMappingURL=Editor.js.map